import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./InvestoContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const frequencyToMonths = {
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

const compoundingPeriodsPerYear = {
  monthly: 12,
  quarterly: 4,
  halfyearly: 2,
  annually: 1,
};

function toNumber(v, fallback = 0) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

const SIPCalculator = () => {
  const content = article.find((item) => item.id === 1);
  const [mode, setMode] = useState("sip");
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [riskLevel, setRiskLevel] = useState("moderate");
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    sipAmount: "1000",
    durationYears: "10",
    expectedReturn: "12.00",
    investFrequency: "monthly",
    compoundingFrequency: "monthly",
    startDate: "",
    stepUp: "10.00",
    inflation: "6.00",
    lumpSum: "100.00",
    taxOnGains: "10.00",
    reinvest: true,
    conversionRate: "1.00",
    expenseRatio: "0.50",
    exitLoad: "1.00",
    goalAmount: "200000",
    volatility: "15.00",
    monthlyIncrease: "0.00",
    annualIncrease: "0.00",
    withdrawalRate: "4.00",
    retirementAge: "60",
    currentAge: "30",
    riskAppetite: "moderate",
    maxDrawdown: "20.00",
    emergencyFund: "6",
    swpAmount: "0.00"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateSIP();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({ 
      ...prev, 
      [name]: type === "checkbox" ? checked : value 
    }));
  };

  const calculateSIP = () => {
    const {
      sipAmount,
      durationYears,
      expectedReturn,
      investFrequency,
      compoundingFrequency,
      stepUp,
      lumpSum,
      inflation,
      taxOnGains,
      reinvest,
      conversionRate,
      expenseRatio,
      exitLoad,
      goalAmount,
      volatility,
      monthlyIncrease,
      annualIncrease,
      withdrawalRate,
      swpAmount
    } = inputs;

    const years = Math.max(1, Math.round(toNumber(durationYears, 1)));
    const monthsTotal = years * 12;

    // Convert percentages
    const annualReturnNominal = toNumber(expectedReturn, 0) / 100;
    const expense = toNumber(expenseRatio, 0) / 100;
    const annualReturnNet = Math.max(-0.9999, annualReturnNominal - expense);

    const tax = toNumber(taxOnGains, 0) / 100;
    const conv = toNumber(conversionRate, 1);
    const step = toNumber(stepUp, 0) / 100;
    const lump = toNumber(lumpSum, 0);
    const vol = toNumber(volatility, 0) / 100;
    const exit = toNumber(exitLoad, 0) / 100;
    const inflationRate = toNumber(inflation, 0) / 100;
    const monthlyInc = toNumber(monthlyIncrease, 0) / 100;
    const annualInc = toNumber(annualIncrease, 0) / 100;
    const withdrawal = toNumber(withdrawalRate, 0) / 100;
    const swp = toNumber(swpAmount, 0);

    const monthlyRate = Math.pow(1 + annualReturnNet, 1 / 12) - 1;
    const freqMonths = frequencyToMonths[investFrequency] || 1;

    // Build timeline arrays
    const baselineSeries = [];
    const bestSeries = [];
    const worstSeries = [];
    const investedSeries = [];
    const withdrawalSeries = [];

    let balanceBaseline = lump;
    let balanceBest = lump;
    let balanceWorst = lump;
    let invested = lump;
    let investedBest = lump;
    let investedWorst = lump;
    let totalWithdrawals = 0;

    // Monthly simulation loop
    for (let m = 0; m <= monthsTotal; m++) {
      if (m === 0) {
        baselineSeries.push({ month: m, value: balanceBaseline });
        bestSeries.push({ month: m, value: balanceBest });
        worstSeries.push({ month: m, value: balanceWorst });
        investedSeries.push({ month: m, value: invested });
        withdrawalSeries.push({ month: m, value: 0 });
        continue;
      }

      // Calculate SIP amount with step-up and increases
      const completedYears = Math.floor((m - 1) / 12);
      let currentSIP = toNumber(sipAmount, 0) * Math.pow(1 + step, completedYears);
      currentSIP *= Math.pow(1 + monthlyInc, m - 1);
      currentSIP *= Math.pow(1 + annualInc, completedYears);

      // Handle SWP (Systematic Withdrawal Plan)
      let monthlyWithdrawal = 0;
      if (swp > 0 && m > 0) {
        monthlyWithdrawal = swp;
        balanceBaseline = Math.max(0, balanceBaseline - monthlyWithdrawal);
        balanceBest = Math.max(0, balanceBest - monthlyWithdrawal);
        balanceWorst = Math.max(0, balanceWorst - monthlyWithdrawal);
        totalWithdrawals += monthlyWithdrawal;
      }

      // Contribution month
      if ((m - 1) % freqMonths === 0) {
        balanceBaseline += currentSIP;
        balanceBest += currentSIP;
        balanceWorst += currentSIP;
        
        invested += currentSIP;
        investedBest += currentSIP;
        investedWorst += currentSIP;
      }

      // Apply growth
      balanceBaseline *= 1 + monthlyRate;
      balanceBest *= 1 + monthlyRate * (1 + vol);
      balanceWorst *= 1 + monthlyRate * (1 - vol);

      // Store data
      baselineSeries.push({ month: m, value: balanceBaseline });
      bestSeries.push({ month: m, value: balanceBest });
      worstSeries.push({ month: m, value: balanceWorst });
      investedSeries.push({ month: m, value: invested });
      withdrawalSeries.push({ month: m, value: totalWithdrawals });
    }

    // Final calculations
    const finalBaselineGross = baselineSeries[baselineSeries.length - 1].value;
    const finalBestGross = bestSeries[bestSeries.length - 1].value;
    const finalWorstGross = worstSeries[worstSeries.length - 1].value;

    // Apply exit load and tax
    const finalBaselineAfterExit = finalBaselineGross * (1 - exit);
    const finalBestAfterExit = finalBestGross * (1 - exit);
    const finalWorstAfterExit = finalWorstGross * (1 - exit);

    const gainBaseline = finalBaselineAfterExit - invested;
    const gainBest = finalBestAfterExit - investedBest;
    const gainWorst = finalWorstAfterExit - investedWorst;

    const postTaxBaseline = finalBaselineAfterExit - Math.max(0, gainBaseline) * tax;
    const postTaxBest = finalBestAfterExit - Math.max(0, gainBest) * tax;
    const postTaxWorst = finalWorstAfterExit - Math.max(0, gainWorst) * tax;

    // Inflation adjustment
    const inflationFactor = Math.pow(1 + inflationRate, years);
    const realValueBaseline = postTaxBaseline / inflationFactor;
    const realValueBest = postTaxBest / inflationFactor;
    const realValueWorst = postTaxWorst / inflationFactor;

    // ROI calculations
    const roiBaseline = (postTaxBaseline - invested) / Math.max(1, invested) * 100;
    const roiBest = (postTaxBest - investedBest) / Math.max(1, investedBest) * 100;
    const roiWorst = (postTaxWorst - investedWorst) / Math.max(1, investedWorst) * 100;

    // Goal achievement
    const goal = toNumber(goalAmount, 0);
    const goalPct = goal > 0 ? (postTaxBaseline / goal) * 100 : null;

    // Retirement calculations
    const currentAge = toNumber(inputs.currentAge, 30);
    const retirementAge = toNumber(inputs.retirementAge, 60);
    const retirementYears = retirementAge - currentAge;
    const monthlyRetirementIncome = postTaxBaseline * withdrawal / 12;

    const resultData = {
      totalInvested: invested,
      finalBaseline: postTaxBaseline,
      finalBest: postTaxBest,
      finalWorst: postTaxWorst,
      realValueBaseline,
      roiBaseline,
      roiBest,
      roiWorst,
      goalPct,
      years,
      baselineSeries,
      bestSeries,
      worstSeries,
      investedSeries,
      withdrawalSeries,
      totalWithdrawals,
      monthlyRetirementIncome,
      retirementYears,
      corpusRequired: monthlyRetirementIncome * 12 / withdrawal,
      wealthAccelerator: roiBaseline / 100
    };

    setResult(resultData);
  };

  const calculateLumpsum = () => {
    const { lumpSum, durationYears, expectedReturn, inflation, taxOnGains } = inputs;
    
    const principal = toNumber(lumpSum, 0);
    const years = toNumber(durationYears, 1);
    const rate = toNumber(expectedReturn, 0) / 100;
    const inflationRate = toNumber(inflation, 0) / 100;
    const tax = toNumber(taxOnGains, 0) / 100;

    const futureValue = principal * Math.pow(1 + rate, years);
    const gain = futureValue - principal;
    const postTaxValue = futureValue - (gain * tax);
    const realValue = postTaxValue / Math.pow(1 + inflationRate, years);

    return {
      principal,
      futureValue,
      postTaxValue,
      realValue,
      gain,
      annualizedReturn: (Math.pow(futureValue / principal, 1/years) - 1) * 100
    };
  };

  const calculateRetirement = () => {
    const { currentAge, retirementAge, monthlyExpenses, inflation } = inputs;
    
    const current = toNumber(currentAge, 30);
    const retirement = toNumber(retirementAge, 60);
    const expenses = toNumber(monthlyExpenses, 5000);
    const inflationRate = toNumber(inflation, 0) / 100;

    const yearsToRetirement = retirement - current;
    const futureMonthlyExpenses = expenses * Math.pow(1 + inflationRate, yearsToRetirement);
    const annualExpenses = futureMonthlyExpenses * 12;
    const corpusRequired = annualExpenses * 25; // 4% withdrawal rule

    return {
      yearsToRetirement,
      futureMonthlyExpenses,
      annualExpenses,
      corpusRequired,
      monthlySavingRequired: corpusRequired / (yearsToRetirement * 12)
    };
  };

  const resetCalculator = () => {
    setInputs({
      sipAmount: "1000",
      durationYears: "10",
      expectedReturn: "12.00",
      investFrequency: "monthly",
      compoundingFrequency: "monthly",
      startDate: "",
      stepUp: "10.00",
      inflation: "6.00",
      lumpSum: "0.00",
      taxOnGains: "10.00",
      reinvest: true,
      conversionRate: "1.00",
      expenseRatio: "0.50",
      exitLoad: "1.00",
      goalAmount: "200000",
      volatility: "15.00",
      monthlyIncrease: "0.00",
      annualIncrease: "0.00",
      withdrawalRate: "4.00",
      retirementAge: "60",
      currentAge: "30",
      riskAppetite: "moderate",
      maxDrawdown: "20.00",
      emergencyFund: "6",
      swpAmount: "0.00"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Total Invested,${result.totalInvested.toFixed(2)}`,
      `Final Value,${result.finalBaseline.toFixed(2)}`,
      `Real Value (Inflation Adj),${result.realValueBaseline.toFixed(2)}`,
      `ROI,${result.roiBaseline.toFixed(2)}%`,
      `Best Case,${result.finalBest.toFixed(2)}`,
      `Worst Case,${result.finalWorst.toFixed(2)}`,
      `Years,${result.years}`,
      `Goal Achievement,${result.goalPct ? result.goalPct.toFixed(2) + '%' : 'N/A'}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sip-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { expectedReturn: "8.00", volatility: "10.00", riskAppetite: "low" },
      moderate: { expectedReturn: "12.00", volatility: "15.00", riskAppetite: "moderate" },
      aggressive: { expectedReturn: "15.00", volatility: "20.00", riskAppetite: "high" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} investment strategy applied`, "info");
  };

  // Chart Data
  const growthChartData = result ? {
    labels: result.baselineSeries.map((p, i) => `Year ${(i / 12).toFixed(1)}`),
    datasets: [
      {
        label: "Worst Case",
        data: result.worstSeries.map(p => p.value),
        borderColor: "#e76f51",
        backgroundColor: "rgba(231,111,81,0.08)",
        fill: true,
        tension: 0.25,
      },
      {
        label: "Expected",
        data: result.baselineSeries.map(p => p.value),
        borderColor: "#28A745",
        backgroundColor: "rgba(40,167,69,0.08)",
        fill: true,
        tension: 0.25,
      },
      {
        label: "Best Case",
        data: result.bestSeries.map(p => p.value),
        borderColor: "#2a9d8f",
        backgroundColor: "rgba(42,157,143,0.08)",
        fill: true,
        tension: 0.25,
      }
    ]
  } : null;

  const allocationChartData = result ? {
    labels: ["Total Invested", "Investment Gains", "Withdrawals"],
    datasets: [{
      data: [
        result.totalInvested,
        result.finalBaseline - result.totalInvested,
        result.totalWithdrawals
      ],
      backgroundColor: ["#19692C", "#28A745", "#FFD93D"],
      borderWidth: 2,
      borderColor: darkMode ? "#1A202C" : "#FFFFFF",
      hoverOffset: 15
    }],
  } : null;

  const lumpsumData = calculateLumpsum();
  const retirementData = calculateRetirement();

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>SIP & Mutual Fund Calculator</h1>
        </div>
        <p>Systematic Investment Plan calculator with advanced projections, retirement planning, and risk analysis</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">SIP Investment</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.finalBaseline >= 0 ? 'positive' : 'negative'}`}>
                        ${calc.finalBaseline.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'SIP Calc' : 'SIP Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "lumpsum" ? "active" : ""}`}
                onClick={() => setActiveTab("lumpsum")}
              >
                <i className="fas fa-money-bill-wave"></i>
                {isMobile ? 'Lumpsum' : 'Lumpsum Calc'}
              </button>
              <button 
                className={`tab ${activeTab === "retirement" ? "active" : ""}`}
                onClick={() => setActiveTab("retirement")}
              >
                <i className="fas fa-umbrella-beach"></i>
                {isMobile ? 'Retirement' : 'Retirement Plan'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Investment Strategy Presets */}
          <div className="presets-section">
            <h4>Investment Strategy:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Mode Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${mode === "sip" ? "active" : ""}`}
                onClick={() => setMode("sip")}
              >
                <i className="fas fa-calendar"></i>
                SIP Mode
              </button>
              <button 
                className={`mode-btn ${mode === "swp" ? "active" : ""}`}
                onClick={() => setMode("swp")}
              >
                <i className="fas fa-hand-holding-usd"></i>
                SWP Mode
              </button>
            </div>
            <div className="mode-description">
              {mode === "sip" 
                ? "Systematic Investment Plan - Regular investing for wealth creation"
                : "Systematic Withdrawal Plan - Regular withdrawals for income"
              }
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Investment Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> SIP Amount</label>
                      <input 
                        type="number" 
                        name="sipAmount" 
                        value={inputs.sipAmount} 
                        onChange={handleChange}
                        placeholder="1000"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Duration (Years)</label>
                      <input 
                        type="number" 
                        name="durationYears" 
                        value={inputs.durationYears} 
                        onChange={handleChange}
                        placeholder="10"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Expected Return (%)</label>
                      <input 
                        type="number" 
                        name="expectedReturn" 
                        value={inputs.expectedReturn} 
                        onChange={handleChange}
                        placeholder="12.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Investment Frequency</label>
                      <select name="investFrequency" value={inputs.investFrequency} onChange={handleChange}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-rotate"></i> Compounding Frequency</label>
                      <select name="compoundingFrequency" value={inputs.compoundingFrequency} onChange={handleChange}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="halfyearly">Half-Yearly</option>
                        <option value="annually">Annually</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Step-Up (%) per Year</label>
                      <input 
                        type="number" 
                        name="stepUp" 
                        value={inputs.stepUp} 
                        onChange={handleChange}
                        placeholder="10.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {/* Advanced Inputs */}
                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Lumpsum Investment</label>
                      <input 
                        type="number" 
                        name="lumpSum" 
                        value={inputs.lumpSum} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Inflation Rate (%)</label>
                      <input 
                        type="number" 
                        name="inflation" 
                        value={inputs.inflation} 
                        onChange={handleChange}
                        placeholder="6.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Tax on Gains (%)</label>
                      <input 
                        type="number" 
                        name="taxOnGains" 
                        value={inputs.taxOnGains} 
                        onChange={handleChange}
                        placeholder="10.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Expense Ratio (%)</label>
                      <input 
                        type="number" 
                        name="expenseRatio" 
                        value={inputs.expenseRatio} 
                        onChange={handleChange}
                        placeholder="0.50"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-door-open"></i> Exit Load (%)</label>
                      <input 
                        type="number" 
                        name="exitLoad" 
                        value={inputs.exitLoad} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bullseye"></i> Goal Amount</label>
                      <input 
                        type="number" 
                        name="goalAmount" 
                        value={inputs.goalAmount} 
                        onChange={handleChange}
                        placeholder="200000"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-bar"></i> Market Volatility (%)</label>
                      <input 
                        type="number" 
                        name="volatility" 
                        value={inputs.volatility} 
                        onChange={handleChange}
                        placeholder="15.00"
                        step="0.01"
                      />
                    </div>
                    {mode === "swp" && (
                      <div className="input-field">
                        <label><i className="fa-solid fa-hand-holding-usd"></i> SWP Amount</label>
                        <input 
                          type="number" 
                          name="swpAmount" 
                          value={inputs.swpAmount} 
                          onChange={handleChange}
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                    )}
                  </div>

                  <div className="input-row">
                    <div className="checkbox-field">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="reinvest" 
                          checked={inputs.reinvest} 
                          onChange={handleChange}
                        />
                        <i className="fa-solid fa-arrows-rotate"></i>
                        Reinvest Dividends
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Lumpsum Tab */}
          {activeTab === "lumpsum" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-money-bill-wave"></i>
                  Lumpsum Investment
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-dollar-sign"></i> Lumpsum Amount</label>
                    <input 
                      type="number" 
                      name="lumpSum" 
                      value={inputs.lumpSum} 
                      onChange={handleChange}
                      placeholder="10000"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-calendar"></i> Inv. Period (yrs)</label>
                    <input 
                      type="number" 
                      name="durationYears" 
                      value={inputs.durationYears} 
                      onChange={handleChange}
                      placeholder="10"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Expected Return (%)</label>
                    <input 
                      type="number" 
                      name="expectedReturn" 
                      value={inputs.expectedReturn} 
                      onChange={handleChange}
                      placeholder="12.00"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Retirement Tab */}
          {activeTab === "retirement" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-umbrella-beach"></i>
                  Retirement Planning
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-user"></i> Current Age</label>
                    <input 
                      type="number" 
                      name="currentAge" 
                      value={inputs.currentAge} 
                      onChange={handleChange}
                      placeholder="30"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-flag"></i> Retirement Age</label>
                    <input 
                      type="number" 
                      name="retirementAge" 
                      value={inputs.retirementAge} 
                      onChange={handleChange}
                      placeholder="60"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-dollar-sign"></i> Monthly Expenses</label>
                    <input 
                      type="number" 
                      name="monthlyExpenses" 
                      value={inputs.monthlyExpenses || "5000"} 
                      onChange={handleChange}
                      placeholder="5000"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Withdrawal Rate (%)</label>
                    <input 
                      type="number" 
                      name="withdrawalRate" 
                      value={inputs.withdrawalRate} 
                      onChange={handleChange}
                      placeholder="4.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-shield"></i> Risk Appetite</label>
                    <select name="riskAppetite" value={inputs.riskAppetite} onChange={handleChange}>
                      <option value="low">Low</option>
                      <option value="moderate">Moderate</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.finalBaseline >= 0 ? 'positive' : 'negative'}`}>
                          <i className="fas fa-chart-line"></i>
                          ${calc.finalBaseline.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> ROI: {calc.roiBaseline.toFixed(2)}%</span>
                        <span><i className="fas fa-calendar"></i> Years: {calc.years}</span>
                        <span><i className="fas fa-bullseye"></i> Goal: {calc.goalPct ? calc.goalPct.toFixed(2) + '%' : 'N/A'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "lumpsum" || activeTab === "retirement") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-line"></i>
                    SIP Investment Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          ${result.finalBaseline.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Final Value
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.totalInvested.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-piggy-bank"></i>
                          Total Invested
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.roiBaseline.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          ROI
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.wealthAccelerator.toFixed(2)}x
                        </div>
                        <div className="result-label">
                          <i className="fas fa-rocket"></i>
                          Wealth Multiplier
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Best Case Value:</span>
                        <span>${result.finalBest.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Worst Case Value:</span>
                        <span>${result.finalWorst.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-inflation"></i> Real Value (Inflation Adj):</span>
                        <span>${result.realValueBaseline.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calendar"></i> Investment Period:</span>
                        <span>{result.years} years</span>
                      </div>
                      {result.goalPct && (
                        <div className="detail-row">
                          <span><i className="fas fa-bullseye"></i> Goal Achievement:</span>
                          <span>{result.goalPct.toFixed(2)}%</span>
                        </div>
                      )}
                    </div>

                    {/* Scenario Analysis */}
                    <div className="risk-summary">
                      <h4>Scenario Analysis</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value positive">
                            ${result.finalBest.toFixed(2)}
                          </div>
                          <div className="risk-label">Best Case (+{inputs.volatility}%)</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            ${result.finalBaseline.toFixed(2)}
                          </div>
                          <div className="risk-label">Expected Case</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value negative">
                            ${result.finalWorst.toFixed(2)}
                          </div>
                          <div className="risk-label">Worst Case (-{inputs.volatility}%)</div>
                        </div>
                      </div>
                    </div>

                    {/* Charts */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-line"></i>
                        Investment Growth Projection
                      </h3>
                      <Line 
                        data={growthChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString();
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div><br/>

                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Investment Breakdown
                      </h3>
                      <Doughnut 
                        data={allocationChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toLocaleString()}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your SIP details to see projections</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "lumpsum" && lumpsumData && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-money-bill-wave"></i>
                  Lumpsum Investment Analysis
                </h3>
                <div className="results-grid">
                  <div className="result-card highlight">
                    <div className="result-value positive">
                      ${lumpsumData.futureValue.toFixed(2)}
                    </div>
                    <div className="result-label">
                      Future Value
                    </div>
                  </div>
                  <div className="result-card">
                    <div className="result-value">
                      ${lumpsumData.principal.toFixed(2)}
                    </div>
                    <div className="result-label">
                      Initial Investment
                    </div>
                  </div>
                  <div className="result-card">
                    <div className="result-value">
                      {lumpsumData.annualizedReturn.toFixed(2)}%
                    </div>
                    <div className="result-label">
                      Annualized Return
                    </div>
                  </div>
                  <div className="result-card">
                    <div className="result-value">
                      ${lumpsumData.realValue.toFixed(2)}
                    </div>
                    <div className="result-label">
                      Real Value (Inflation Adj)
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "retirement" && retirementData && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-umbrella-beach"></i>
                  Retirement Planning Analysis
                </h3><br/>
                <div className="retirement-results">
                  <div className="results-grid">
                    <div className="result-card highlight">
                      <div className="result-value">
                        ${retirementData.corpusRequired.toLocaleString()}
                      </div>
                      <div className="result-label">
                        Retirement Corpus Required
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        {retirementData.yearsToRetirement}
                      </div>
                      <div className="result-label">
                        Years to Retirement
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        ${retirementData.futureMonthlyExpenses.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Future Monthly Expenses
                      </div>
                    </div>
                    <div className="result-card">
                      <div className="result-value">
                        ${retirementData.monthlySavingRequired.toFixed(2)}
                      </div>
                      <div className="result-label">
                        Monthly Saving Required
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default SIPCalculator;